import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Dropdown, OverflowMenu, OverflowMenuItem, Theme } from '@carbon/react';
import {
  ArrowRight,
  CheckboxChecked,
  Checkmark,
  ChevronDown,
  List,
  User,
} from '@carbon/icons-react';
import axiosInstance from '../../utils/axiosInstance';
import { useTheme } from '../../contexts/ThemeContext';
import Collapsible from '../Collapsible';
import { pluralForm } from '../workday/workDayUtils';
import TaskDetailsPanel from './TaskDetailsPanel';
import AssignTaskPanel from './AssignTaskPanel';
import './TodayTasksPanel.css';

// Backend šalje vreme u UTC - prikazujemo ga u CET/CEST (Evropa/Belgrade, Srbija)
const TIME_ZONE = 'Europe/Belgrade';

const TABS = [
  { id: 'all', label: 'Svi' },
  { id: 'unassigned', label: 'Nedodeljeni' },
  { id: 'in_progress', label: 'U toku' },
  { id: 'completed', label: 'Završeni' },
];

const formatTime = (iso) => {
  if (!iso) return '--:--';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--:--';
  return new Intl.DateTimeFormat('sr-RS', {
    timeZone: TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
};

const toLocalDateInput = (date) => {
  const d = date || new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Izvlači niz iz različitih oblika odgovora
const extractArray = (res) => {
  const body = res.data;
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  if (body && Array.isArray(body.value)) return body.value;
  if (body && Array.isArray(body.$values)) return body.$values;
  if (body && Array.isArray(body.items)) return body.items;
  if (body && body.data && Array.isArray(body.data.$values)) return body.data.$values;
  if (body && body.data && Array.isArray(body.data.items)) return body.data.items;
  return [];
};

// Novi format: `data` je lista kategorija [{ category, count, tasks }].
// Stari (ravni) format se toleriše kao jedna grupa bez kategorije.
const extractGroups = (res) => {
  const list = extractArray(res);
  if (list.length === 0) return [];
  const isGrouped = list.every((item) => item && Array.isArray(item.tasks));
  if (!isGrouped) return [{ category: null, count: list.length, tasks: list }];
  return list.map((group) => ({
    category: group.category || null,
    count: typeof group.count === 'number' ? group.count : group.tasks.length,
    tasks: group.tasks,
  }));
};

const groupKey = (group, index) => group.category || `__all-${index}`;

const isUnassigned = (task) => !task?.assignToAll && !task?.assignedToName;

// Ime korisnika (rezidenta) - iz polja, ili iz naslova oblika "Jelo - Ime Prezime"
const getResidentName = (task) => {
  if (task?.residentName) return task.residentName;
  const title = task?.title || '';
  const idx = title.lastIndexOf(' - ');
  return idx > -1 ? title.slice(idx + 3).trim() : '';
};

// Naziv zadatka/obroka bez imena korisnika (naslov je oblika "Pesto pasta - Vidak Bodiroža")
const getTaskLabel = (task) => {
  const title = (task?.title || '').trim();
  const resident = task?.residentName;
  if (resident && title.endsWith(` - ${resident}`)) {
    return title.slice(0, title.length - resident.length - 3).trim();
  }
  const idx = title.lastIndexOf(' - ');
  return idx > -1 ? title.slice(0, idx).trim() : title;
};

// Soba iz opisa zadatka ("Jelo, Ime Prezime, soba 066, Vreme izvršenja: ...")
const getRoomName = (task) => {
  const match = (task?.description || '').match(/soba\s+([^,]+)/i);
  return match ? match[1].trim() : '';
};

// Tekst zaduženja: "Nedodeljeno" ili ime zaposlenog
const getAssignmentLabel = (task) => {
  if (isUnassigned(task)) return 'Nedodeljeno';
  if (task?.assignToAll) return 'Svi zaposleni';
  return task?.assignedToName || 'Nedodeljeno';
};

const passesTab = (task, tabId) => {
  if (tabId === 'unassigned') return isUnassigned(task);
  if (tabId === 'in_progress') return task?.status === 'in_progress';
  if (tabId === 'completed') return task?.status === 'completed';
  return true;
};

// Jedan red zadatka: korisnik / jelo · soba / vreme · zaduženje.
// Klik (ili Enter/Space) na red otvara desni panel sa detaljima; checkbox menja selekciju.
const TaskRow = ({ task, selectable, selected, onToggle, onOpen }) => {
  const resident = getResidentName(task);
  const taskLabel = getTaskLabel(task);
  const room = getRoomName(task);
  const detail = room ? `${taskLabel} · Soba ${room}` : taskLabel;

  return (
    <div
      className={`adash-row ${selected ? 'adash-row--selected' : ''}`}
      role="button"
      tabIndex={0}
      title={`Otvori detalje: ${task.title}`}
      onClick={() => onOpen(task)}
      onKeyDown={(event) => {
        // Tasteri pritisnuti na dečijim kontrolama (checkbox) ne otvaraju detalje
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(task);
        }
      }}
    >
      {selectable ? (
        <button
          type="button"
          className={`adash-row__check ${selected ? 'is-checked' : ''}`}
          onClick={(event) => {
            event.stopPropagation();
            onToggle(task.id);
          }}
          onKeyDown={(event) => event.stopPropagation()}
          aria-pressed={selected}
          aria-label={selected ? 'Poništi izbor zadatka' : 'Odaberi zadatak'}
        >
          {selected && <Checkmark size={14} />}
        </button>
      ) : (
        <span className="adash-row__check adash-row__check--done" aria-hidden="true">
          <Checkmark size={14} />
        </span>
      )}

      <span className="adash-row__text">
        <span className="adash-row__title">{resident || taskLabel}</span>
        {resident && taskLabel && (
          <span className="adash-row__sub">{detail}</span>
        )}
        <span className="adash-row__meta">
          <span className="adash-row__time">{formatTime(task.scheduledTime)}</span>
          <span className="adash-row__dot" aria-hidden="true">·</span>
          <span className="adash-row__assign">{getAssignmentLabel(task)}</span>
        </span>
      </span>
    </div>
  );
};

const TodayTasksPanel = ({ onNavigateTasks }) => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [isExpanded, setIsExpanded] = useState(true);
  // Kategorije koje su skupljene - po učitavanju su sve skupljene (vidi fetch ispod)
  const [collapsedCategories, setCollapsedCategories] = useState(() => new Set());
  // Izabrani zadatak za desni sidebar sa detaljima
  const [selectedTask, setSelectedTask] = useState(null);
  // Filter po oblasti ('' = sve oblasti)
  const [areas, setAreas] = useState([]);
  const [areaFilter, setAreaFilter] = useState('');
  // Selekcija zadataka (za masovnu dodelu) i otvoren desni panel za dodelu
  const [selectedIds, setSelectedIds] = useState([]);
  const [isAssignPanelOpen, setIsAssignPanelOpen] = useState(false);
  const { theme } = useTheme();

  // Da li je PC prikaz (>= 769px). Na PC-u su lebdeći dugmići ("Označi sve" i
  // "Dodeli") vidljivi stalno, a na mobilnom se prikazuju uz selekciju.
  const [isDesktopView, setIsDesktopView] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(min-width: 769px)').matches
      : true
  );

  // Skrolovanje tabova - aktivni tab se dovodi u vidno polje (kao u profilu stanara)
  const tabListRef = useRef(null);
  const prevTabIndexRef = useRef(TABS.findIndex((t) => t.id === 'all'));

  useEffect(() => {
    const listEl = tabListRef.current;
    if (!listEl) return;
    const activeTabEl = listEl.querySelector('.adash-tab--active');
    if (!activeTabEl) return;

    const currentIndex = TABS.findIndex((t) => t.id === activeTab);
    const prevIndex = prevTabIndexRef.current;
    const goingRight = currentIndex >= prevIndex;

    // getBoundingClientRect za tačno pozicioniranje nezavisno od roditelja
    const listRect = listEl.getBoundingClientRect();
    const tabRect = activeTabEl.getBoundingClientRect();

    const targetScrollLeft = goingRight
      // Udesno: poravnaj levu ivicu aktivnog taba sa levom ivicom liste
      ? listEl.scrollLeft + (tabRect.left - listRect.left)
      // Ulevo: poravnaj desnu ivicu aktivnog taba sa desnom ivicom liste
      : listEl.scrollLeft + (tabRect.right - listRect.right);

    listEl.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    prevTabIndexRef.current = currentIndex;
  }, [activeTab]);

  // Pri promeni taba ili filtera oblasti, prethodna selekcija i otvoreni paneli se resetuju
  useEffect(() => {
    setSelectedIds([]);
    setSelectedTask(null);
    setIsAssignPanelOpen(false);
  }, [activeTab, areaFilter]);

  // Dok je nešto izabrano ili je otvoren panel, sakrij "Idi na vrh" strelicu (mobilni)
  useEffect(() => {
    const active = selectedIds.length > 0 || selectedTask != null || isAssignPanelOpen;
    document.body.classList.toggle('admin-tasks--selection-active', active);
    return () => document.body.classList.remove('admin-tasks--selection-active');
  }, [selectedIds, selectedTask, isAssignPanelOpen]);

  // Prati prelaz između PC i mobilnog prikaza (zbog prikaza lebdećih dugmića)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(min-width: 769px)');
    const handleChange = (event) => setIsDesktopView(event.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', handleChange);
      return () => mql.removeEventListener('change', handleChange);
    }
    // Stariji browseri (Safari < 14)
    mql.addListener(handleChange);
    return () => mql.removeListener(handleChange);
  }, []);

  // Učitaj oblasti za filter dropdown
  useEffect(() => {
    let isMounted = true;

    const fetchAreas = async () => {
      try {
        const res = await axiosInstance.get('/api/Area');
        if (isMounted) setAreas(extractArray(res));
      } catch (err) {
        console.error('Greška pri učitavanju oblasti:', err);
      }
    };

    fetchAreas();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchTasks = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams();
        params.append('date', toLocalDateInput(new Date()));
        if (areaFilter) params.append('areaId', areaFilter);
        const response = await axiosInstance.get(`/api/Task/by-date?${params.toString()}`);
        const list = extractGroups(response);
        if (isMounted) {
          setGroups(list);
          // Sve kategorije su po defaultu skupljene
          setCollapsedCategories(new Set(list.map((group, index) => groupKey(group, index))));
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          if (isMounted) {
            setGroups([]);
            setCollapsedCategories(new Set());
          }
        } else {
          console.error('Greška pri učitavanju zadataka za dashboard:', err);
          if (isMounted) setError('Greška pri učitavanju zadataka');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchTasks();
    return () => {
      isMounted = false;
    };
  }, [areaFilter]);

  const allTasks = useMemo(
    () => groups.flatMap((group) => (Array.isArray(group.tasks) ? group.tasks : [])),
    [groups]
  );

  const counts = useMemo(() => ({
    all: allTasks.length,
    unassigned: allTasks.filter((t) => isUnassigned(t)).length,
    in_progress: allTasks.filter((t) => t?.status === 'in_progress').length,
    completed: allTasks.filter((t) => t?.status === 'completed').length,
  }), [allTasks]);

  // Zadrži grupisanje po kategorijama; u svakoj kategoriji primeni filter taba.
  const filteredGroups = useMemo(
    () => groups
      .map((group) => ({
        category: group.category,
        tasks: (Array.isArray(group.tasks) ? group.tasks : []).filter((t) => passesTab(t, activeTab)),
      }))
      .filter((group) => group.tasks.length > 0),
    [groups, activeTab]
  );

  // Zadaci su izborni u svim tabovima osim "Završeni" (isto kao na nurse/caregiver dashboard-u)
  const selectable = activeTab !== 'completed';

  // ID-ovi zadataka koji ulaze u masovnu selekciju ("Označi sve"): samo zadaci iz
  // RAZVIJENIH kategorija (sklopljene kategorije se preskaču - isto kao na "Zadaci danas").
  const visibleIds = useMemo(
    () =>
      filteredGroups
        .filter((group, index) => !collapsedCategories.has(groupKey(group, index)))
        .flatMap((group) => (Array.isArray(group.tasks) ? group.tasks : []))
        .map((task) => task?.id)
        .filter((id) => id != null),
    [filteredGroups, collapsedCategories]
  );
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const areaOptions = useMemo(
    () => [{ id: '', name: 'Sve oblasti' }, ...areas],
    [areas]
  );
  const selectedArea =
    areaOptions.find((area) => String(area.id) === String(areaFilter)) || areaOptions[0];

  const toggleCategory = (key) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Stabilan handler za checkbox - funkcionalni setState znači da se referenca ne
  // menja između rendera, pa memoizovani redovi ne re-renderuju bez potrebe.
  const toggleTask = useCallback((id) => {
    if (id == null) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const clearSelection = useCallback(() => setSelectedIds([]), []);

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) return prev.filter((id) => !visibleIds.includes(id));
      return Array.from(new Set([...prev, ...visibleIds]));
    });
  }, [allVisibleSelected, visibleIds]);

  // Klik na red otvara detalje zadatka (i zatvara panel za dodelu, ako je otvoren)
  const handleOpenTask = useCallback((task) => {
    setIsAssignPanelOpen(false);
    setSelectedTask(task);
  }, []);

  // "Dodeli" otvara desni panel za dodelu izabranih zadataka (i zatvara detalje, ako su otvoreni)
  const handleOpenAssignPanel = useCallback(() => {
    setSelectedTask(null);
    setIsAssignPanelOpen(true);
  }, []);
  const handleCloseAssignPanel = useCallback(() => setIsAssignPanelOpen(false), []);

  // Posle uspešne dodele: označi zadatke kao dodeljene (optimistično), očisti
  // selekciju i zatvori panel - bez ponovnog učitavanja cele liste.
  const handleAssigned = useCallback((employee) => {
    setGroups((prev) => prev.map((group) => ({
      ...group,
      tasks: (Array.isArray(group.tasks) ? group.tasks : []).map((task) =>
        selectedIds.includes(task.id)
          ? { ...task, assignToAll: false, assignedToId: employee.id, assignedToName: employee.name }
          : task
      ),
    })));
    setSelectedIds([]);
    setIsAssignPanelOpen(false);
  }, [selectedIds]);

  const goToTasks = () => {
    if (onNavigateTasks) onNavigateTasks();
    else navigate('/tasks');
  };

  return (
    <section className="adash-panel adash-tasks">
      <button
        type="button"
        className={`adash-panel__toggle ${isExpanded ? 'is-expanded' : ''}`}
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <span className="adash-panel__toggle-left">
          <span className="adash-panel__icon"><List size={20} /></span>
          <span className="adash-panel__title">Zadaci danas</span>
        </span>
        <ChevronDown
          size={20}
          className={`adash-panel__chevron ${isExpanded ? 'is-expanded' : ''}`}
        />
      </button>

      <Collapsible open={isExpanded}>
        <div className="adash-tasks__body">
          <div className="adash-tasks__filters">
            <div className="adash-tasks__area-filter">
              <Dropdown
                id="adash-area-filter"
                className="adash-area-dropdown"
                size="sm"
                titleText="Oblast"
                hideLabel
                label="Sve oblasti"
                items={areaOptions}
                itemToString={(item) => (item ? item.name : '')}
                selectedItem={selectedArea}
                onChange={({ selectedItem }) =>
                  setAreaFilter(selectedItem ? String(selectedItem.id) : '')
                }
              />
            </div>

            <OverflowMenu
              id="adash-tasks-actions"
              className="adash-tasks__actions"
              size="sm"
              iconDescription="Akcije"
              ariaLabel="Akcije"
            >
              <OverflowMenuItem itemText="Dodeli zadatke" onClick={goToTasks} />
            </OverflowMenu>
          </div>

          <div className="adash-tasks__toolbar">
            <div className="adash-tabs" role="tablist" aria-label="Filter zadataka" ref={tabListRef}>
              {TABS.map((tab) => {
                const count = counts[tab.id];
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`adash-tab ${isActive ? 'adash-tab--active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <span className="adash-tab__label">{tab.label}</span>
                    {count > 0 && <span className="adash-tab__count">{count}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="adash-table">
            <div className="adash-table__head" aria-hidden="true">
              <span className="adash-table__head-check" />
              <span>Zadatak</span>
            </div>

            {loading ? (
              <div className="adash-tasks__skeleton">
                {[0, 1, 2, 3, 4].map((i) => (
                  <span className="adash-tasks__skeleton-row" key={i} />
                ))}
              </div>
            ) : error ? (
              <div className="adash-tasks__empty">{error}</div>
            ) : filteredGroups.length === 0 ? (
              <div className="adash-tasks__empty">Nema zadataka</div>
            ) : (
              filteredGroups.map((group, index) => {
                const key = groupKey(group, index);
                const isCollapsed = collapsedCategories.has(key);
                return (
                  <div className="adash-category" key={key}>
                    {group.category && (
                      <button
                        type="button"
                        className={`adash-category__header ${isCollapsed ? '' : 'is-expanded'}`}
                        onClick={() => toggleCategory(key)}
                        aria-expanded={!isCollapsed}
                      >
                        <span className="adash-category__title">{group.category}</span>
                        <span className="adash-category__count">{group.tasks.length}</span>
                        <ChevronDown
                          size={16}
                          className={`adash-category__chevron ${isCollapsed ? '' : 'is-expanded'}`}
                        />
                      </button>
                    )}
                    <Collapsible open={!isCollapsed}>
                      <div className="adash-category__items">
                        {group.tasks.map((task, taskIndex) => (
                          <TaskRow
                            key={task.id || `${task.title}-${taskIndex}`}
                            task={task}
                            selectable={selectable}
                            selected={task?.id != null && selectedIds.includes(task.id)}
                            onToggle={toggleTask}
                            onOpen={handleOpenTask}
                          />
                        ))}
                      </div>
                    </Collapsible>
                  </div>
                );
              })
            )}
          </div>

          <button type="button" className="adash-panel__link" onClick={goToTasks}>
            <span className="adash-panel__link-label">
              <List size={16} />
              Prikaži sve zadatke
            </span>
            <ArrowRight size={16} className="adash-panel__link-arrow" />
          </button>
        </div>
      </Collapsible>

      {/* Traka selekcije (PC): broj izabranih zadataka + akcija "Dodeli" */}
      {selectedIds.length > 0 && (
        <div className="adash-selection">
          <span className="adash-selection__info">
            {selectedIds.length === 1
              ? 'Izabran 1 zadatak'
              : `Izabrana ${selectedIds.length} ${pluralForm(selectedIds.length, 'zadatak', 'zadatka', 'zadataka')}`}
          </span>
          <div className="adash-selection__actions">
            <button
              type="button"
              className="adash-selection__clear"
              onClick={clearSelection}
            >
              Poništi
            </button>
            <button
              type="button"
              className="adash-selection__primary"
              onClick={handleOpenAssignPanel}
            >
              {`Dodeli ${selectedIds.length} ${pluralForm(selectedIds.length, 'zadatak', 'zadatka', 'zadataka')}`}
            </button>
          </div>
        </div>
      )}

      {/* Lebdeći (FAB) dugmići - na PC-u stalno dostupni, na mobilnom kad je
          nešto izabrano ("Označi sve" levo, akciono dugme desno). */}
      {selectable && !isAssignPanelOpen &&
        (selectedIds.length > 0 || (isDesktopView && visibleIds.length > 0)) &&
        ReactDOM.createPortal(
        <Theme theme={theme}>
          {/* "Označi sve" se prikazuje samo ako postoji bar jedan zadatak u
              razvijenoj kategoriji (sklopljene kategorije se ne obuhvataju). */}
          {visibleIds.length > 0 && (isDesktopView || selectedIds.length > 0) && (
            <button
              type="button"
              className="adash-fab adash-fab--select-all"
              onClick={allVisibleSelected ? clearSelection : toggleSelectAllVisible}
              aria-label={allVisibleSelected ? 'Poništi izbor' : 'Označi sve zadatke'}
            >
              <CheckboxChecked size={22} />
              <span>{allVisibleSelected ? 'Poništi sve' : 'Označi sve'}</span>
            </button>
          )}

          {selectedIds.length > 0 && (
            <button
              type="button"
              className="adash-fab adash-fab--primary"
              onClick={handleOpenAssignPanel}
              aria-label="Dodeli zadatke"
            >
              <User size={22} />
              <span>Dodeli ({selectedIds.length})</span>
            </button>
          )}
        </Theme>,
        document.body
      )}

      {/* Desni sidebar sa detaljima izabranog zadatka */}
      <TaskDetailsPanel task={selectedTask} onClose={() => setSelectedTask(null)} />

      {/* Desni sidebar za dodelu izabranih zadataka */}
      <AssignTaskPanel
        open={isAssignPanelOpen}
        taskIds={selectedIds}
        onClose={handleCloseAssignPanel}
        onAssigned={handleAssigned}
      />
    </section>
  );
};

export default TodayTasksPanel;
